import { selectStyle } from "Redux/appSlice";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import {
  addAnswer,
  addChapter,
  addStep,
  askToDelete,
  changeRichText,
  deleteElement,
  renameAnswer,
  renameChapter,
  renameStep,
  setAddElement,
  setCurrentStep,
  setGuideExample,
  setRichTextFocus,
  setShowPopUp,
} from "feature/producer/actions";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { Question } from "types/type.module";
import { ElementTag, StepContent } from "types/types";
import AddButton from "../Components/Atoms/AddButton";
import Button from "../Components/Atoms/Button";
import ElementSelector from "../Components/Molecule/ElementSelector";
import PopUp from "../Components/Molecule/PopUp";
import PopUpAddElements from "../Components/Molecule/PopUpAddElements";
import QuestionProducer from "../Components/Molecule/QuestionProducer";
import SearchBar from "../Components/Molecule/SearchBar";
import SidePage from "../Components/Molecule/SidePage";
import StepSelector from "../Components/Molecule/StepSelector";
import Layout from "../Pages/Layout";
import {
  changeQuestionInBackend,
  initializeModule,
  removeChapterInBackend,
  removePossibleAnswerInBackend,
  removeQuestionInBackend,
  selectConditionToChange,
  selectCurrentModule,
  selectCurrentStep,
  selectDeleteElement,
  selectLinkToAdd,
  selectPopUpType,
  selectProducerNewBeEvents,
  selectRefToAdd,
  selectRichTextFocus,
  selectShowAddElement,
  selectShowPopUp,
  selectTermToAdd,
  setConditionToChange,
} from "../Redux/ProducerSlice";
import { addUserElementToBackend, resetLoadingAnimation, selectLoadingAnimation, setLoadingAnimation } from "../Redux/UserSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-regular-svg-icons";

/**
  @function ProducerModule
  render producer module that renders chapters in modules
  @returns {React.Node}
*/

export default function ProducerModule() {
  const params = useParams();
  const producer = useAppSelector((state) => state.producer);
  const conditionToChange = useAppSelector(selectConditionToChange);
  const currentStep = useAppSelector(selectCurrentStep);
  const currentModule = useAppSelector(selectCurrentModule);
  const newBeEvents = useAppSelector(selectProducerNewBeEvents);
  const richTextFocus = useAppSelector(selectRichTextFocus);
  const elementToDelete = useAppSelector(selectDeleteElement);
  const showPopUp = useAppSelector(selectShowPopUp);
  const popUpType = useAppSelector(selectPopUpType);
  const termToAdd = useAppSelector(selectTermToAdd);
  const linkToAdd = useAppSelector(selectLinkToAdd);
  const refToAdd = useAppSelector(selectRefToAdd);
  const loadingAnimation = useAppSelector(selectLoadingAnimation);
  const { color1, color2 } = useAppSelector(selectStyle);
  const dispatch = useAppDispatch();
  const isQuestionMode = currentStep !== "";

  const { orgId = "", moduleId = "" } = params;

  useEffect(() => {
    dispatch(setLoadingAnimation());
    dispatch(
      initializeModule({
        orgId,
        moduleId,
      })
    ).then(() => {
      dispatch(resetLoadingAnimation());
    });
    dispatch(setCurrentStep({ currentStep: "" }));
  }, []);

  useEffect(() => {
    if (conditionToChange?.deletedStatus) {
      conditionToChange.toAdd.forEach(async (c) => {
        let question: Record<string, any> = {};
        currentModule?.chapters.forEach((cha) => {
          if (cha.chapter_id === c.ids.chapter) {
            cha.questions.forEach((q) => {
              if (q.question_id === c.ids.question) {
                question = q;
              }
            });
          }
        });

        dispatch(
          changeQuestionInBackend({
            orgId,
            questionId: question.question_id,
            trigger: question.trigger,
            content: question.content,
            conclusion: question.conclusion,
            displayId: question.display_id,
            multiple: question.multiple,
            print: question.print,
            should_be_hidden: question.should_be_hidden,
            guidances: question.guidances,
            examples: question.examples,
            newBeEvents: newBeEvents,
            // comment: comment,
          })
        );
      });
      dispatch(setConditionToChange());
    }
  }, [conditionToChange?.deletedStatus]);

  const chapters = (
    <div className="pt-44 ">
      {currentModule?.chapters && (
        <Chapters
          onChapterChange={(val, i, id) => {
            dispatch(
              renameChapter({
                chapterPosition: i,
                newName: val,
                chapterId: id,
              })
            );
          }}
          onQuestionChange={(val, i, q_i, questionId) => {
            dispatch(
              renameStep({
                chapterPosition: i,
                stepPosition: q_i,
                value: val,
                stepId: questionId,
              })
            );
          }}
          onAdd={(c) => {
            dispatch(addChapter({ chapterPosition: c + 1 }));
          }}
          onSelect={(q) => {
            dispatch(
              setCurrentStep({
                currentStep: q.question_id
                  ? q.question_id
                  : q.frontQuestionId || "",
              })
            );
          }}
          onAddQuestion={(
            type: "question" | "conclusion",
            pos: number,
            i: number
          ) => {
            dispatch(addStep({ type: type, position: pos, chapter: i }));
          }}
        />
      )}
    </div>
  );

  const questionsList = currentModule?.chapters
    ? currentModule.chapters.flatMap((c, c_i) => {
        let questions: any = [{ chapter: c.name }];

        questions = [
          ...questions,
          ...c.questions.map((q, q_i) => {
            const extend = { chapter_index: c_i, question_index: q_i } as const;
            return { ...q, ...extend };
          }),
        ];

        return questions;
      })
    : [];

  const currentQuestionIndex =
    currentStep !== ""
      ? questionsList.findIndex(
          (q) =>
            q.question_id === currentStep || q.frontQuestionId === currentStep
        )
      : 0;
  
  if (loadingAnimation) {
    return (
      <Layout>
        <div className="m-auto flex h-screen w-screen flex-col justify-center">
          <FontAwesomeIcon icon={faCircle} bounce size="4x" />
        </div>
      </Layout>
    );
  }

  return (
    <div>
      <Layout
        barAddOns={
          <div className="flex flex-row justify-between">
            <div className="flex-col justify-between items-center">
              <SearchBar />
              {currentModule && (
                <div className="text-center text-white">
                  {currentModule.name}
                </div>
              )}
              {currentModule && currentStep != "" && (
                <div className="text-center text-white">
                  {
                    currentModule.chapters.filter(
                      (elem) =>
                        elem.questions.filter(
                          (q) =>
                            q.question_id === currentStep ||
                            q.frontQuestionId === currentStep
                        ).length > 0
                    )[0].name
                  }
                </div>
              )}
            </div>

            {color1 && !isQuestionMode && (
              <Button
                bgColor={color2}
                textColor={color1}
                onClick={() => dispatch(setAddElement())}
                label="Add Element"
                className="text-sm !p-2 !m-auto col-start-3 col-end-4"
              />
            )}
          </div>
        }
      >
        {richTextFocus &&
          Object.keys(richTextFocus).length > 0 &&
          richTextFocus.chapter !== undefined && <SidePannel />}
        {elementToDelete !== undefined && (
          <PopUp
            text={
              <>
                are you sure you want to delete element?
                {conditionToChange && "toAdd" in conditionToChange && (
                  <>
                    {conditionToChange.toAdd.length > 0 ? (
                      <>
                        <br />
                        this will modify the following questions logic:
                        <br />
                        {conditionToChange.toAdd.map((c, i) => (
                          <div key={i}>
                            {c.label} <br />
                          </div>
                        ))}
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </>
            }
            onContinue={async () => {
              if (elementToDelete?.element === "chapter") {
                dispatch(
                  removeChapterInBackend({
                    orgId: params.orgId || "",
                    chapterId: elementToDelete.chapterId || "",
                    frontChapterId: elementToDelete.frontChapterId || "",
                  })
                );
              }

              if (elementToDelete?.element === "question") {
                dispatch(
                  removeQuestionInBackend({
                    orgId: params.orgId || "",
                    questionId: elementToDelete.questionId || "",
                    frontQuestionId: elementToDelete.frontQuestionId || "",
                  })
                );
              }

              if (elementToDelete && elementToDelete.element === "answer") {
                dispatch(
                  removePossibleAnswerInBackend({
                    orgId: params.orgId || "",
                    possibleAnswerId: elementToDelete.possibleAnswerId || "",
                    frontAnswerId: elementToDelete.frontAnswerId || "",
                  })
                );
              }

              if (elementToDelete?.element === "trigger") {
                dispatch(
                  deleteElement({
                    directions: elementToDelete.directions || [],
                    chapter: elementToDelete.chapter || 0,
                    step: elementToDelete.step || 0,
                  })
                );
              }

              dispatch(askToDelete(undefined));
            }}
            onCancel={() => dispatch(askToDelete(undefined))}
          />
        )}
        {showPopUp && color1 && (
          <PopUpAddElements
            type={popUpType}
            onSet={(type, val) => {
              const resolveCallback = () => {
                if (type === "Term") {
                  return termToAdd?.callback;
                }
                if (type === "Link") {
                  return linkToAdd?.callback;
                }
                if (type === "Reference") {
                  return refToAdd?.callback;
                }
              };
              dispatch(
                addUserElementToBackend({
                  orgId: params.orgId || "",
                  elementData: val,
                  // @ts-ignore
                  type: type,
                  callback: resolveCallback(),
                })
              );
            }}
            onCancel={() => dispatch(setShowPopUp())}
          />
        )}
        <div id="hiddenElement" className="hidden"></div>
        <div className="h-full ">
          {isQuestionMode ? (
            <QuestionComponent
              questionsList={questionsList}
              currentQuestionIndex={currentQuestionIndex}
            />
          ) : (
            chapters
          )}
        </div>
      </Layout>
    </div>
  );
}

/**
   @function SidePannel
   side pannel is rendered for content
*/
function SidePannel() {
  const richTextFocus = useAppSelector(selectRichTextFocus);
  const currentModule = useAppSelector(selectCurrentModule);
  const dispatch = useAppDispatch();

  let content: any = {};
  if (richTextFocus && richTextFocus.answer === undefined) {
    if (
      richTextFocus.guide !== undefined &&
      currentModule &&
      richTextFocus.chapter !== undefined &&
      richTextFocus.question !== undefined
    ) {
      content =
        currentModule.chapters[richTextFocus.chapter].questions[
          richTextFocus.question
        ].guidances[richTextFocus.guide].content;
    } else if (
      richTextFocus.example !== undefined &&
      currentModule &&
      richTextFocus.chapter !== undefined &&
      richTextFocus.question !== undefined
    ) {
      content =
        currentModule.chapters[richTextFocus.chapter].questions[
          richTextFocus.question
        ].examples[richTextFocus.example].content;
    } else if (
      currentModule &&
      richTextFocus.chapter !== undefined &&
      richTextFocus.question !== undefined
    ) {
      content =
        currentModule.chapters[richTextFocus.chapter].questions[
          richTextFocus.question
        ].content;
    }
  }

  if (richTextFocus && richTextFocus.answer !== undefined) {
    if (
      richTextFocus.guide !== undefined &&
      currentModule &&
      richTextFocus.chapter !== undefined &&
      richTextFocus.question !== undefined
    ) {
      content =
        currentModule.chapters[richTextFocus.chapter].questions[
          richTextFocus.question
        ].possible_answers[richTextFocus.answer].guidances[richTextFocus.guide]
          .content;
    } else if (
      richTextFocus.example !== undefined &&
      currentModule &&
      richTextFocus.chapter !== undefined &&
      richTextFocus.question !== undefined
    ) {
      content =
        currentModule.chapters[richTextFocus.chapter].questions[
          richTextFocus.question
        ].possible_answers[richTextFocus.answer].examples[richTextFocus.example]
          .content;
    } else if (
      currentModule &&
      richTextFocus.chapter !== undefined &&
      richTextFocus.question !== undefined
    ) {
      content =
        currentModule.chapters[richTextFocus.chapter].questions[
          richTextFocus.question
        ].possible_answers[richTextFocus.answer].content;
    }
  }

  if (content !== null) {
    return (
      <SidePage onClose={() => dispatch(setRichTextFocus({}))}>
        <ElementSelector
          content={content}
          onUpdate={(i: any) => dispatch(changeRichText(i))}
        />
      </SidePage>
    );
  }
  return <></>;
}

/* 
     @function getQuestionObjectSimilarToSnapshot 
     returns Question object with the keys that are consistent with the snapshot that is saved
     @param question {Object} - Question Object
   */
const getQuestionObjectSimilarToSnapshot = (question: any) => {
  const quesCopy = { ...question };
  // making content key's value stringified to make it similar to snapshot object
  if (typeof quesCopy.content === "string") {
    quesCopy.content = JSON.parse(quesCopy.content);
  }
  // removing extra keys
  delete quesCopy.chapter_index;
  delete quesCopy.question_index;
  return quesCopy;
};

/*
  @functions renderSteps
  render steps with all the answers
  @param questionList {array} - question to render
  @param currentQuestionIndex {num} - current question index
  
*/
function QuestionComponent({
  questionsList,
  currentQuestionIndex,
}: {
  questionsList: any[];
  currentQuestionIndex: number;
}) {
  const dispatch = useAppDispatch();

  return (
    <div className="relative  overflow-x-hidden h-full ">
      <button
        className="absolute sticky top-24 left-4 z-50"
        onClick={() => {
          dispatch(setCurrentStep({ currentStep: "" }));
        }}
      >
        <ArrowBack />
      </button>
      <Virtuoso
        totalCount={questionsList.length}
        initialTopMostItemIndex={currentQuestionIndex}
        itemContent={(i) => {
          if (questionsList[i].chapter) {
            return (
              <div className="m-auto text-center text-3xl mt-16">
                {questionsList[i].chapter}
              </div>
            );
          }
          return <MemoizedQuestion data={questionsList} index={i} key={i} />;
        }}
      />
    </div>
  );
}

const MemoizedQuestion = React.memo(
  ({ data, index }: { data: any; index: number }) => {
    const q = data[index];
    const dispatch = useAppDispatch();
    const currentModule = useAppSelector(selectCurrentModule);

    return (
      <div className=" my-22 py-44" key={index}>
        <QuestionProducer
          question={q}
          index={q.question_index}
          onAnswerChange={(val: any, answer_id, frontAnswerId) => {
            dispatch(
              renameAnswer({
                newName: val,
                answer: answer_id,
                frontAnswerId: frontAnswerId,
              })
            );
          }}
          onQuestionChange={(_, val: any, question_id) => {
            dispatch(
              renameStep({
                chapterPosition:
                  currentModule?.chapters[q.chapter_index].order_number || 0,
                value: val,
                stepPosition: q.order_number,
                stepId: question_id,
              })
            );
          }}
          onAddAnswer={(order_number) =>
            dispatch(
              addAnswer({
                chapter: q.chapter_index,
                step: q.question_index,
                order_number: order_number,
              })
            )
          }
          onGuideExampleChange={(
            type,
            val: any,
            currentElement,
            isQuestion,
            id
          ) => {
            dispatch(
              setGuideExample({
                chapterPosition: q.chapter_index,
                questionPosition: q.question_index,
                answerPosition: currentElement,
                type: type,
                value: val,
                isQuestion: isQuestion,
                id: id,
              })
            );
          }}
          createQuestionSnapshot={getQuestionObjectSimilarToSnapshot}
        />
      </div>
    );
  }
);

MemoizedQuestion.displayName = "Question";

const ArrowBack = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" height="48" width="48">
      <path d="M24 40 8 24 24 8l2.1 2.1-12.4 12.4H40v3H13.7l12.4 12.4Z" />
    </svg>
  );
};

function Chapters({
  onChapterChange,
  onQuestionChange,
  onAdd,
  onSelect,
  onAddQuestion,
}: {
  onChapterChange: (
    val: any,
    order_number: number,
    chapter_id: string,
    frontChapterId: string
  ) => void;
  onQuestionChange: (
    val: { type: ElementTag; content: StepContent[] },
    order_number: number,
    question_order_number: number,
    questionId?: string
  ) => void;
  onAdd: (order_number: number) => void;
  onSelect: (question: Question) => void;
  onAddQuestion: (
    type: "question" | "conclusion",
    pos: number,
    order_number: number
  ) => void;
}) {
  const module = useAppSelector(selectCurrentModule);
  const showAddElement = useAppSelector(selectShowAddElement);
  if (!module) return null;

  return (
    <div className="!overflow-y-scroll h-[80vh] ">
      {showAddElement && (
        <div className="m-auto flex flex-col items-center">
          <AddButton
            onClick={() => {
              onAdd(0);
            }}
            isAbsolute={false}
          />
        </div>
      )}
      {module.chapters.map((c, i) => (
        <div key={i} id="chapter">
          <StepSelector
            chapter={c}
            chapterIndex={i}
            onChapterChange={(
              val,
              order_number,
              chapter_id,
              frontChapterId
            ) => {
              onChapterChange(val, order_number, chapter_id, frontChapterId);
            }}
            onQuestionChange={(q_order_number, val, questionId) =>
              onQuestionChange(val, c.order_number, q_order_number, questionId)
            }
            onAdd={() => onAdd(c.order_number)}
            onSelect={(q) => {
              onSelect(q);
            }}
            onAddQuestion={(type: any, pos) =>
              onAddQuestion(type, pos, c.order_number)
            }
            createQuestionSnapshot={getQuestionObjectSimilarToSnapshot}
          />
        </div>
      ))}
    </div>
  );
}
